import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Layers, ArrowDown, Repeat, CalendarDays, Coins, BookOpen, TrendingDown } from 'lucide-react';
import CloRiskReturnBlock from '@/components/CloRiskReturnBlock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reckoner's fund-of-funds "nesting doll" chains — feeder → core.
const NESTING = {
  RAAR: ['RAAY', 'RAAA'],
  RAAY: ['RAAA'],
  RCLR: ['RCLY', 'RCLO'],
  RCLY: ['RCLO'],
  RAAA: [],
  RCLO: [],
};
const FAMILY = { RAAR: 'AAA', RAAY: 'AAA', RAAA: 'AAA', RCLR: 'BBB-B', RCLY: 'BBB-B', RCLO: 'BBB-B' };
const SIBLINGS = { AAA: ['RAAA', 'RAAY', 'RAAR'], 'BBB-B': ['RCLO', 'RCLY', 'RCLR'] };

const TIERS = [
  { key: 'senior', label: 'Senior · AAA / AA', risk: 'Lowest risk · paid first', tone: 'emerald' },
  { key: 'mezz', label: 'Mezzanine · A / BBB / BB / B', risk: 'Middle of the waterfall', tone: 'amber' },
  { key: 'equity', label: 'Equity · unrated', risk: 'First loss · last paid', tone: 'rose' },
];

function detectTier(name = '') {
  const n = name.toUpperCase();
  if (/\bAAA\b|SENIOR DEBT|FIRST PRIORITY/.test(n) && !/BBB|AA-BB|AA-BBB/.test(n)) return { key: 'senior', label: 'AAA senior tranches' };
  if (/BBB-B|B-BBB|BBB|AA-BB|AA-BBB/.test(n)) return { key: 'mezz', label: n.match(/BBB-B|B-BBB|AA-BB|AA-BBB|BBB/)[0].replace('B-BBB', 'BBB-B') + ' mezzanine tranches' };
  return { key: 'senior', label: 'mostly investment-grade tranches' };
}

function detectPayout(name = '', ticker = '') {
  const n = name.toUpperCase();
  if (n.includes('REINVEST') || ['RAAR', 'RCLR'].includes(ticker)) return { key: 'reinvest', label: 'Reinvesting — no cash distributions', icon: Repeat, blurb: 'Interest is rolled straight back into NAV. Nothing hits your account, so a taxable brokerage sees no monthly 1099 income — the return shows up as share-price growth instead.' };
  if (n.includes('ANNUAL') || ['RAAY', 'RCLY'].includes(ticker)) return { key: 'annual', label: 'Annual — one payout in December', icon: CalendarDays, blurb: 'Income is deferred all year and paid as a single year-end distribution, so the trailing yield reads 0.00% for eleven months even though the fund is earning every day.' };
  return { key: 'monthly', label: 'Monthly distributions', icon: Coins, blurb: 'Standard bond-ETF behaviour: the floating-rate coupons collected from the loan pool are passed through to you every month and taxed as ordinary income.' };
}

const TONE = {
  emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-700',
  amber: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-700',
  rose: 'bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-900/40 dark:text-rose-100 dark:border-rose-700',
  muted: 'bg-white/60 text-slate-500 border-slate-200 dark:bg-black/20 dark:text-slate-400 dark:border-slate-700',
};

// Dovish = the market is pricing Fed cuts: 2Y below 3M (inverted front
// end) or the 3M bill down 15bp+ vs the prior print. Mirrors the Fed &
// Treasury Watch card's read so the two never disagree.
function useRateCutWatch() {
  const [watch, setWatch] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/macro`, { timeout: 4000 })
      .then((r) => {
        if (cancelled) return;
        const ty = r.data?.series?.treasury_yields?.latest || {};
        const m3 = ty.yield_3_month; const y2 = ty.yield_2_year; const m3Prior = ty.prior?.yield_3_month;
        if (m3 == null || y2 == null) return;
        const inverted = y2 < m3 - 0.05;
        const falling = m3Prior != null && m3Prior - m3 >= 0.15;
        if (inverted || falling) {
          setWatch({
            m3, y2,
            reason: inverted
              ? `2Y Treasury ${y2.toFixed(2)}% sits below the 3M bill ${m3.toFixed(2)}% — the market is pricing Fed cuts`
              : `3M bill fell ${(m3Prior - m3).toFixed(2)} pts to ${m3.toFixed(2)}% since the prior print`,
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return watch;
}

export default function CloExplainerCard({ stock, onSelectStock }) {
  const ticker = (stock?.ticker || '').toUpperCase();
  const rateCut = useRateCutWatch();
  const name = stock?.name || '';
  const tier = detectTier(name);
  const payout = detectPayout(name, ticker);
  const chain = NESTING[ticker];
  const family = FAMILY[ticker];
  const PayIcon = payout.icon;

  return (
    <div
      className="mt-3 rounded-lg border p-3 border-sky-300/70 bg-sky-50/70 dark:bg-sky-950/20 dark:border-sky-700/60"
      data-testid="modal-clo-explainer"
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 mt-0.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200 border border-sky-300/60 dark:border-sky-700/60">
          CLO
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-sky-800 dark:text-sky-300">Collateralized Loan Obligation ETF</span>
            <span className="text-[11px] text-muted-foreground">Floating-rate corporate loan pool · {tier.label}</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
            A CLO bundles hundreds of senior-secured <strong className="text-foreground">leveraged corporate loans</strong> into one
            security and slices it into rated tranches. Cash flows down a &ldquo;waterfall&rdquo;: senior AAA holders are paid first, the
            unrated equity slice eats the first defaults. Because the loans are <strong className="text-foreground">floating rate</strong>,
            payouts rise with Fed hikes instead of losing value like fixed-rate bonds.
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]" data-testid="clo-waterfall">
        {TIERS.map((t, i) => {
          const active = t.key === tier.key;
          return (
            <div key={t.key} className={`p-2 rounded border ${active ? TONE[t.tone] : TONE.muted} ${active ? 'ring-2 ring-offset-1 ring-sky-400/60 dark:ring-offset-slate-900' : ''}`} data-testid={`clo-tier-${t.key}${active ? '-active' : ''}`}>
              <div className="flex items-center gap-1 font-semibold">
                {i > 0 && <ArrowDown className="w-3 h-3 opacity-60" />}
                <Layers className="w-3 h-3" />
                {t.label}
                {active && <span className="ml-auto text-[9px] font-bold uppercase tracking-wider">{ticker} sits here</span>}
              </div>
              <div className="opacity-80 mt-0.5">{t.risk}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 p-2 rounded border border-sky-200/70 dark:border-sky-800/50 bg-white/60 dark:bg-black/20 text-[11px]" data-testid={`clo-payout-${payout.key}`}>
        <div className="flex items-center gap-1.5 font-semibold text-sky-900 dark:text-sky-200"><PayIcon className="w-3.5 h-3.5" />{payout.label}</div>
        <div className="text-muted-foreground mt-0.5 leading-relaxed">{payout.blurb}</div>
      </div>

      {/* Sep 2026 — Risk & return block. Fetches 1y/2y/3y CAGR,
          volatility, and Sharpe from `/api/stocks/{ticker}/clo-metrics`,
          plus the same metrics for the sister fund when a pairing is
          known. Includes the two-sided rate-cut narrative and a "Show
          the math" collapsible with the three formulas. */}
      <CloRiskReturnBlock ticker={ticker} />

      {rateCut && (
        <div className="mt-3 p-2 rounded border border-amber-300/70 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-950/30 text-[11px]" data-testid="clo-rate-cut-alert">
          <div className="flex items-center gap-1.5 font-semibold text-amber-900 dark:text-amber-200"><TrendingDown className="w-3.5 h-3.5" />Rate-cut watch — floating coupons will reset lower</div>
          <div className="text-amber-800/90 dark:text-amber-300/80 mt-0.5 leading-relaxed">
            {rateCut.reason}. CLO loans pay SOFR + spread, so each Fed cut trims this fund&rsquo;s income within a quarter — {payout.key === 'monthly' ? 'expect the monthly distribution to drift down' : 'NAV will compound more slowly'}. Price should stay steady; it is the yield that moves. Cross-check the <Link to="/dashboard" className="underline font-semibold">Fed &amp; Treasury Watch</Link> card.
          </div>
        </div>
      )}

      {chain && (
        <div className="mt-3 p-2 rounded border border-violet-300/60 dark:border-violet-700/50 bg-violet-50/70 dark:bg-violet-950/30 text-[11px]" data-testid="clo-nesting-doll">
          <div className="font-semibold text-violet-900 dark:text-violet-200">Nesting-doll structure · Reckoner {family} suite</div>
          <div className="text-muted-foreground mt-0.5 leading-relaxed">
            {chain.length
              ? <>{ticker} doesn&rsquo;t buy CLO paper directly — ~99% of its assets are shares of the next fund down. Trace the chain and the core fund is the one holding the actual AAA/BBB loan tranches.</>
              : <>{ticker} is the <strong className="text-foreground">core fund</strong> that actually holds the CLO tranches; the Annual and Reinvesting siblings own it as their single holding to change <em>when</em> you get paid, not <em>what</em> you own.</>}
          </div>
          <div className="mt-1.5 flex items-center gap-1 flex-wrap font-mono">
            {(chain.length ? [ticker, ...chain] : SIBLINGS[family]).map((t, i, arr) => (
              <span key={t} className="inline-flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => t !== ticker && onSelectStock?.(t)}
                  className={`px-1.5 py-0.5 rounded border text-[11px] font-bold ${t === ticker ? 'bg-violet-600 text-white border-violet-600' : 'bg-white dark:bg-slate-900 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700 hover:bg-violet-100 dark:hover:bg-violet-900/50'}`}
                  data-testid={`clo-chain-${t}`}
                  disabled={t === ticker}
                >
                  {t}
                </button>
                {i < arr.length - 1 && <span className="text-violet-400">{chain.length ? '→ holds' : '·'}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap text-[10px]">
        <span className="italic text-sky-700/80 dark:text-sky-400/70">
          {payout.key === 'monthly'
            ? 'Distributions are interest income — taxed as ordinary income in a taxable account; shielded inside a Roth IRA.'
            : 'Trailing yield reads 0.00% by design — judge this fund on total return / NAV growth, not dividend yield.'}
        </span>
        <Link to="/learn/clo-etfs-nesting-doll" className="inline-flex items-center gap-1 font-semibold text-sky-700 dark:text-sky-300 hover:underline" data-testid="clo-learn-link">
          <BookOpen className="w-3 h-3" /> Learn: CLO ETFs &amp; the nesting-doll trick
        </Link>
      </div>
    </div>
  );
}
